import { useEffect, useState, useCallback, useRef } from 'react';
import { useFormik } from 'formik';
import MetaTags from 'react-meta-tags';
import Breadcrumbs from '../../../../components/Common/Breadcrumb';
import { useDispatch } from 'react-redux';
import jwt_decode from 'jwt-decode';
import Swal from 'sweetalert2';

import { BuildingInspectionListTable } from './Components/BuildingInspectionListTable';
import { PropertySelector } from './Components/PropertySelector';
import { GenerateBuildingInspectionModal } from './Components/GenerateBuildingInspectionModal';
import { DeleteBuildingInspectionConfirmationModal } from './Components/DeleteBuildingInspectionConfirmationModal';

import {
  defaultBuildingInspection,
  getBuildingInspectionInitialValues,
  BuildingInspectionValidationSchema,
} from './Components/FormikHelpers';

import { getPropertyList } from '../../../Inventory/pages/FacilityInventory/Helpers/Api';
import { validateStep, validateDraft } from './Components/FormikHelpers/ValidationSchema';

import {
  createBuildingInspection,
  getBuildingInspectionList,
  updateBuildingInspection,
  deleteBuildingInspection,
} from './Components/FormikHelpers/Api';
import { setLoading } from '../../../../store/actions';
import { clearDraftBackup, draftBackupKey, loadDraftBackup } from '../../../../hooks/draftBackup';

const SECTIONS = ['interior', 'elevators', 'underground', 'outside', 'misc_others'];

const normalizePictures = (items = []) =>
  items.map((item) => ({
    ...item,
    pictures: (item.pictures || []).map((photo) => {
      if (typeof photo === 'string') return photo;
      return photo?.url || photo?.fileUrl || photo;
    }),
  }));

// The payload the server sees. For an autosaved draft, custom rows that have no
// name yet are left out: the server requires a name, and a row somebody is
// still typing into is not worth failing the whole save over.
const buildPayload = (values, { draft = false } = {}) => {
  const payload = { ...values };
  SECTIONS.forEach((section) => {
    let items = normalizePictures(values[section]);
    if (draft) items = items.filter((it) => !(it.is_custom && !(it.name || '').trim()));
    payload[section] = items;
  });
  return payload;
};

// Whether a local backup holds anything a person would miss.
const backupHasContent = (values) => {
  if (!values) return false;
  if ((values.conclusions_recommendations || '').trim()) return true;
  return SECTIONS.some((section) =>
    (values[section] || []).some(
      (it) =>
        it?.needs_work === true ||
        (it?.comments || '').trim() ||
        (Array.isArray(it?.attachments) && it.attachments.length > 0) ||
        (it?.is_custom && (it?.name || '').trim())
    )
  );
};

const BuildingInspection = () => {
  const dispatch = useDispatch();

  const [properties, setProperties] = useState([]);
  const [inspections, setInspections] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isAutoFilling, setIsAutoFilling] = useState(false);
  const [user, setUser] = useState(null);
  const [selectedInspection, setSelectedInspection] = useState(null);
  // A draft created by autosave for a *new* inspection. Kept apart from
  // selectedInspection so role gating still treats the form as "new".
  const [autoDraftId, setAutoDraftId] = useState(null);
  const autoDraftIdRef = useRef(null);
  const [openCount, setOpenCount] = useState(0);

  const [selectedFilters, setSelectedFilters] = useState({
    property_id: '',
    building_name: '',
    // All months by default. The old default (the month's name, e.g. "September")
    // matched no option and the server read it as "this year only".
    month: '',
    status: 'all',
  });

  const getUserFromToken = useCallback(() => {
    try {
      const token = localStorage.getItem('accessToken');
      return token ? jwt_decode(token) : null;
    } catch {
      return null;
    }
  }, []);

  const fetchProperties = useCallback(async () => {
    const currentUser = getUserFromToken();
    if (!currentUser) return;
    setUser(currentUser);

    try {
      dispatch(setLoading(true));
      const result = await getPropertyList(currentUser?.role || '', currentUser?.id || '');
      setProperties(result?.propertyList || []);
    } catch {
      Swal.fire('Error', 'Failed to fetch properties', 'error');
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch, getUserFromToken]);

  const fetchInspections = useCallback(
    async (propertyId, building_name, month, status = 'all', { quiet = false } = {}) => {
      try {
        if (!quiet) dispatch(setLoading(true));
        const params = {
          propertyId: propertyId || undefined,
          building_name: building_name || undefined,
          month: month || undefined,
          status: status || 'all',
        };

        const response = await getBuildingInspectionList(params);
        setInspections(response?.data || response || []);
      } catch (error) {
        console.error('Failed to fetch building inspections:', error);
        setInspections([]);
      } finally {
        if (!quiet) dispatch(setLoading(false));
      }
    },
    [dispatch]
  );

  const { property_id, building_name, month } = selectedFilters;

  const loadInspections = useCallback(
    (opts) => {
      fetchInspections(property_id, building_name, month, selectedFilters.status, opts);
    },
    [fetchInspections, property_id, building_name, month, selectedFilters.status]
  );

  const setAutoDraft = (id) => {
    autoDraftIdRef.current = id;
    setAutoDraftId(id);
  };

  const currentRecordId = (values) =>
    selectedInspection?._id || values?._id || autoDraftIdRef.current || null;

  const submitInspection = async (values) => {
    const finalizing = values.status === 'completed';
    const isValid = finalizing ? await validateStep(values) : await validateDraft(values);
    if (!isValid) {
      throw new Error('Validation failed');
    }

    const payload = buildPayload(values, { draft: !finalizing });
    delete payload._id;
    delete payload.completed_by;
    delete payload.finalizedAt;
    delete payload.history;

    const id = currentRecordId(values);
    if (id) {
      await updateBuildingInspection(id, payload);
      return id;
    }

    const response = await createBuildingInspection(payload);
    setAutoDraft(response._id);
    return response._id;
  };

  // Background autosave. Creates the draft the first time, patches it after.
  const autosaveDraft = async (values) => {
    const payload = { ...buildPayload(values, { draft: true }), status: 'draft' };
    delete payload._id;
    delete payload.completed_by;
    delete payload.finalizedAt;
    delete payload.history;

    const id = currentRecordId(values);
    if (id) {
      await updateBuildingInspection(id, payload);
      return id;
    }
    try {
      const created = await createBuildingInspection(payload);
      setAutoDraft(created._id);
      loadInspections({ quiet: true });
      return created._id;
    } catch (err) {
      const message = (err && err.message) || '';
      if (/already exists|conflict/i.test(message)) {
        const paused = new Error(`${message}. Change the date to resume autosave.`);
        paused.code = 'PAUSE';
        throw paused;
      }
      throw err;
    }
  };

  const formik = useFormik({
    initialValues: defaultBuildingInspection,
    validationSchema: BuildingInspectionValidationSchema,
    enableReinitialize: true,
  });

  const backupKey = draftBackupKey(
    'building-inspection',
    selectedFilters.property_id,
    selectedFilters.building_name || '',
    selectedInspection?._id || 'new'
  );

  const handleAddClick = async () => {
    setSelectedInspection(null);
    setAutoDraft(null);

    const fresh = {
      ...defaultBuildingInspection,
      property_details: {
        property_id: selectedFilters.property_id,
        building_name: selectedFilters.building_name,
      },
    };

    let values = fresh;
    const key = draftBackupKey(
      'building-inspection',
      selectedFilters.property_id,
      selectedFilters.building_name || '',
      'new'
    );
    const backup = loadDraftBackup(key);
    if (backup && !backup.serverId && backupHasContent(backup.values)) {
      const when = backup.savedAt
        ? new Date(backup.savedAt).toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
          })
        : 'earlier';
      const result = await Swal.fire({
        title: 'Restore unsaved work?',
        text: `You have an unfinished inspection for this building from ${when} that never reached the server.`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Restore',
        cancelButtonText: 'Start fresh',
        confirmButtonColor: '#0e578e',
        reverseButtons: true,
      });
      if (result.isConfirmed) {
        values = {
          ...fresh,
          ...backup.values,
          property_details: fresh.property_details,
          status: 'draft',
        };
        delete values._id;
      } else {
        clearDraftBackup(key);
      }
    } else if (backup) {
      // The draft is already on the server and in the list; the copy is stale.
      clearDraftBackup(key);
    }

    formik.resetForm({ values });
    setOpenCount((n) => n + 1);
    setIsModalOpen(true);
  };

  const handleEditClick = async (inspection) => {
    setSelectedInspection(inspection);
    setAutoDraft(null);

    try {
      setIsAutoFilling(true);
      const values = await getBuildingInspectionInitialValues(inspection._id);

      formik.setValues({
        ...values,
        _id: inspection._id,
        property_details: values.property_details || {
          property_id: selectedFilters.property_id,
          building_name: selectedFilters.building_name,
        },
      });
    } catch (err) {
      Swal.fire('Error', 'Failed to load inspection details for editing', 'error');
    } finally {
      setIsAutoFilling(false);
      setOpenCount((n) => n + 1);
      setIsModalOpen(true);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedInspection?._id) return;

    try {
      dispatch(setLoading(true));
      await deleteBuildingInspection(selectedInspection._id);
      Swal.fire('Deleted', 'Building Inspection deleted successfully', 'success');
      setIsDeleteModalOpen(false);
      setSelectedInspection(null);
      fetchInspections(selectedFilters.property_id, selectedFilters.building_name, selectedFilters.month, selectedFilters.status);
    } catch (error) {
      Swal.fire('Error', 'Failed to delete inspection', 'error');
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleDraftDiscarded = () => {
    setAutoDraft(null);
    loadInspections({ quiet: true });
  };

  const handlePropertyChange = (newFilters) => {
    setSelectedFilters(newFilters);
  };

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  useEffect(() => {
    loadInspections();
  }, [loadInspections]);

  const refreshInspections = loadInspections;

  return (
    <div className="page-content">
      <MetaTags>
        <title>Rentdigicare | Building Inspections</title>
      </MetaTags>

      <div className="container-fluid">
        <Breadcrumbs
          title="Maintenance"
          breadcrumbItem="Building Inspections"
        />

        <PropertySelector
          propertyList={properties}
          selectedFilters={selectedFilters}
          onPropertySelectionChange={handlePropertyChange}
          onAddClick={handleAddClick}
          isLoading={isAutoFilling}
          user={user}
        />

        <BuildingInspectionListTable
          items={inspections}
          onDelete={(inspection) => {
            setSelectedInspection(inspection);
            setIsDeleteModalOpen(true);
          }}
          onEdit={handleEditClick}
          userRole={user?.role || 'other'}
          onRefresh={() =>
            fetchInspections(selectedFilters.property_id, selectedFilters.building_name, selectedFilters.month, selectedFilters.status)
          }
        />

        <GenerateBuildingInspectionModal
          formik={formik}
          filters={selectedFilters}
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          isEdit={!!selectedInspection}
          onRefresh={refreshInspections}
          submitInspection={submitInspection}
          autosaveDraft={autosaveDraft}
          autoDraftId={autoDraftId}
          onDraftDiscarded={handleDraftDiscarded}
          backupKey={backupKey}
          openCount={openCount}
          userRole={user?.role || 'other'}
          userId={user?.id || ''}
        />

        <DeleteBuildingInspectionConfirmationModal
          open={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onDelete={handleDeleteConfirm}
          inspection={selectedInspection}
        />
      </div>
    </div>
  );
};

export { BuildingInspection };
